import { cn } from '@mdvip/shared/lib/utils';
import { CUSTOM_ICONS } from './customIcons';

/**
 * Semantic icon name → Font Awesome 7 Pro glyph.
 *
 * Mirrors the design system's `FA_GLYPH` map (Payment Center `runtime.jsx`) so our names match
 * the design's, plus the glyphs the two portal app-shells need that the Payment Center prototype
 * never exercised (grouped below). Add a name here when a feature needs a new glyph — the full FA
 * Pro glyph map is already shipped in `styles/fontawesome-pro.css`, so any FA name resolves.
 */
const FA_GLYPH = {
  // Design system map (Payment Center)
  layoutGrid: 'grid-2',
  messageSquare: 'message',
  bookOpen: 'book-open',
  creditCard: 'credit-card',
  search: 'magnifying-glass',
  bell: 'bell',
  chevronDown: 'chevron-down',
  chevronRight: 'chevron-right',
  chevronLeft: 'chevron-left',
  check: 'check',
  circleCheck: 'circle-check',
  x: 'xmark',
  plus: 'plus-large',
  minus: 'minus',
  pencil: 'pencil',
  trash: 'trash-can',
  info: 'circle-info',
  fileText: 'file-lines',
  fileArrowDown: 'file-arrow-down',
  filePdf: 'file-pdf',
  mail: 'envelope',
  receipt: 'receipt',
  calendar: 'calendar',
  clock: 'clock',
  clockRotateLeft: 'clock-rotate-left',
  alertTriangle: 'triangle-exclamation',
  alertCircle: 'circle-exclamation',
  shieldCheck: 'shield-check',
  piggyBank: 'piggy-bank',
  landmark: 'landmark',
  wallet: 'wallet',
  mapPin: 'location-dot',
  house: 'house',
  refreshCw: 'arrows-rotate',
  download: 'download',
  upload: 'upload',
  print: 'print',
  sun: 'sun',
  moon: 'moon',
  sliders: 'sliders',
  dollarSign: 'dollar-sign',
  stethoscope: 'stethoscope',
  plane: 'plane',
  utensils: 'utensils',
  dumbbell: 'dumbbell',
  lock: 'lock',
  arrowRight: 'arrow-right',
  arrowLeft: 'arrow-left',
  calendarDays: 'calendar-days',
  calendarCheck: 'calendar-check',
  phone: 'phone',
  mobileScreen: 'mobile-screen',
  command: 'command',
  menu: 'bars',
  user: 'user',
  // App-shell additions (CNP-228) — nav, offline indicator, external links
  bookmark: 'bookmark',
  building: 'building',
  chevronUp: 'chevron-up',
  externalLink: 'arrow-up-right-from-square',
  gauge: 'gauge',
  logOut: 'right-from-bracket',
  textSize: 'font',
  userCog: 'user-gear',
  users: 'users',
  wifi: 'wifi',
  wifiOff: 'wifi-slash',
  ellipsisVertical: 'ellipsis-vertical',
  // Busy/loading indicator (pair with `weight="solid"` and an `animate-spin` className)
  spinner: 'circle-notch',
  // Status toast leading icons. The other three (circleCheck, alertTriangle, info) are already
  // above; only the error mark was missing, since nothing else needed a circled x.
  circleXmark: 'circle-xmark',
  // Resources page tiles — Community section only, the two of the design's 7 tile icons not
  // already present above (bookOpen, utensils, dumbbell, users, landmark).
  pen: 'pen',
  newspaper: 'newspaper',
  // Profile photo upload trigger (AvatarBadge's camera badge).
  camera: 'camera',
  // Help & Support Contact card — website link icon (its "opens externally" arrow reuses the
  // existing externalLink glyph rather than adding a second one for the same meaning).
  globe: 'globe',
  // Refer a Friend (CNP-783) — Send button, matching the Claude Design handoff's own local
  // FA_GLYPH map (features/modal-refer-friend/ModalReferFriend.jsx) exactly.
  paperPlane: 'paper-plane',
  // Providers primary navigation — the AWP Program bottom-tab slot.
  clipboardList: 'clipboard-list',
  // Providers primary navigation — the Reports bottom-tab slot.
  chartColumn: 'chart-column',
  // Resource Center — the Follow MDVIP tile's badge.
  shareNodes: 'share-nodes',
  // Sortable table headers — neutral, then the active direction.
  sort: 'sort',
  sortUp: 'sort-up',
  sortDown: 'sort-down',
  // The one save/bookmark glyph across the portal (BookmarkButton) — the design's ButtonSave
  // compound defaults to this, not the bookmark-ribbon glyph.
  heart: 'heart',
} as const;

/**
 * Semantic name → Font Awesome 7 Brands glyph. Mirrors the design system's own `FA_BRAND` map.
 * Brand marks ship in one face only, so `weight` does not apply.
 */
const FA_BRAND = {
  // `xTwitter`, not `x`: that name is already the close mark.
  xTwitter: 'x-twitter',
  facebook: 'facebook',
  youtube: 'youtube',
  linkedin: 'linkedin',
  instagram: 'instagram',
} as const;

const FA_ALL = { ...FA_GLYPH, ...FA_BRAND };

export type IconGlyphName = keyof typeof FA_GLYPH;
export type IconBrandName = keyof typeof FA_BRAND;
export type IconCustomName = keyof typeof CUSTOM_ICONS;
export type IconName = IconGlyphName | IconBrandName | IconCustomName;

/** Every supported glyph name (handy for galleries, pickers, and enumeration). */
export const ICON_NAMES = Object.keys(FA_GLYPH) as IconGlyphName[];

/** Every supported brand-mark name. Separate from `ICON_NAMES` because these ignore `weight`. */
export const BRAND_ICON_NAMES = Object.keys(FA_BRAND) as IconBrandName[];

/** Every custom (non-Font Awesome) mark's name. These are SVGs drawn at one weight, so they ignore `weight`. */
export const CUSTOM_ICON_NAMES = Object.keys(CUSTOM_ICONS) as IconCustomName[];

const isCustomIcon = (name: IconName): name is IconCustomName => name in CUSTOM_ICONS;

/**
 * FA Pro weight (face). Default is `regular`; affirmative/emphasis marks typically use `solid`.
 * Only the three self-hosted faces are exposed — we don't ship the `thin` (100) woff2.
 */
export type IconWeight = 'light' | 'regular' | 'solid';

/** Icon-font glyphs scale with `font-size`, so size maps to a font-size utility (not width/height). */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

const SIZE_CLASS: Record<IconSize, string> = {
  xs: 'text-xs', // 12px
  sm: 'text-sm', // 14px
  md: 'text-base', // 16px
  lg: 'text-lg', // 18px
  xl: 'text-xl', // 20px
  '2xl': 'text-2xl', // 24px
  // 32px, the design's tile-glyph size. Tailwind's own text-3xl is 30px; rem keeps it scaling with font size.
  '3xl': 'text-[2rem]',
};

/** The square `1em` box every icon sits in, so a glyph and a custom mark take the same footprint. */
const ICON_BOX =
  'pointer-events-none inline-flex size-[1em] shrink-0 items-center justify-center align-middle leading-none';

export interface IconProps {
  /** Semantic icon name: a Font Awesome 7 Pro glyph, a Brands mark, or a custom SVG mark. */
  name: IconName;
  /** FA Pro face. Defaults to `regular`. Ignored for brand marks and custom marks, which ship one face only. */
  weight?: IconWeight;
  /** Glyph size as a font-size token. Defaults to `md` (16px). Override freely via `className`. */
  size?: IconSize;
  /** Extra classes — typically a color token (`text-muted-foreground`) or a size override. */
  className?: string;
  /**
   * Accessible label. When provided the icon is exposed to assistive tech (`role="img"`);
   * otherwise it is treated as decorative (`aria-hidden`) — the default, since most icons sit
   * beside a visible text label.
   */
  label?: string;
}

/**
 * Renders a Font Awesome 7 Pro glyph or a Brands mark as an `<i>`, and a custom mark (`customIcons.tsx`)
 * as an inline `<svg>`. Either inherits `currentColor`, so `text-*` color utilities tint it, and scales
 * with the `size` token (font-size). The glyphs are built on the self-hosted FA Pro webfonts + glyph CSS
 * in `styles/fontawesome-pro.css`.
 */
export function Icon({ name, weight = 'regular', size = 'md', className, label }: IconProps) {
  const a11y = label ? ({ role: 'img', 'aria-label': label } as const) : ({ 'aria-hidden': true } as const);

  if (isCustomIcon(name)) {
    const { svg, paths } = CUSTOM_ICONS[name];
    return (
      // biome-ignore lint/a11y/noSvgWithoutTitle: decorative marks are aria-hidden and labelled ones carry role="img" + aria-label, so no <title>
      <svg {...svg} className={cn(ICON_BOX, SIZE_CLASS[size], className)} {...a11y}>
        {paths}
      </svg>
    );
  }

  const isBrand = name in FA_BRAND;
  return (
    <i
      className={cn(
        isBrand ? 'fa-brands' : `fa-${weight}`,
        `fa-${FA_ALL[name]}`,
        SIZE_CLASS[size],
        // FA's own `.fa { width: 1.25em }` is unlayered and beats Tailwind's width, so pin a square
        // glyph box via FA's `--fa-width` knob (matches lucide/the design's square icon footprint).
        ICON_BOX,
        '[--fa-width:1em]',
        className,
      )}
      {...a11y}
    />
  );
}
